import {
  Product,
  CreateProductDto,
  UpdateProductDto,
  UpdateStatusDto,
  UpdateQuantityDto,
  ProductAuditLog,
  Ingredient,
  Order,
  CreateOrderDto,
  OrderEditHistory,
} from "../types";
import { runtimeConfig } from "../config/runtimeConfig";
import { supplierAuthService } from "./auth.service";

// API Configuration
export const SUPPLIER_API_URL = runtimeConfig.supplierApiUrl;
const API_BASE_URL = SUPPLIER_API_URL;

// Helper function to add auth headers to all requests
const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
  const token = supplierAuthService.getAccessToken();
  const headers = new Headers(options.headers || {});

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    supplierAuthService.clearSession();
    supplierAuthService.redirectToLogin();
  }

  return response;
};

// Products API Service
export const productsApi = {
  // Get all products with optional filters
  getProducts: async (params?: {
    supplierId?: number;
    status?: string;
    availability?: boolean;
    search?: string;
  }): Promise<Product[]> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Product/GetMyProducts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    });
    if (!response.ok) throw new Error("Failed to fetch products");
    let data = await response.json();
    
    // Apply filters on client side since backend GetAll returns everything
    if (params?.status) data = data.filter((p: Product) => p.status === params.status);
    if (params?.availability !== undefined) data = data.filter((p: Product) => p.availability === params.availability);
    if (params?.search) {
      const searchLower = params.search.toLowerCase();
      data = data.filter((p: Product) => 
        (p.name && p.name.toLowerCase().includes(searchLower)) || 
        (p.productCode && p.productCode.toLowerCase().includes(searchLower))
      );
    }
    
    return data;
  },

  // Get product by ID
  getProduct: async (id: number): Promise<Product> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Product/GetById`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(id)
    });
    if (!response.ok) throw new Error("Failed to fetch product");
    return response.json();
  },

  // Get product by code
  getProductByCode: async (productCode: string): Promise<Product> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Product/GetMyProducts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    });
    if (!response.ok) throw new Error("Failed to fetch product");
    const data = await response.json();
    const product = data.find((p: Product) => p.productCode === productCode);
    if (!product) throw new Error("Product not found");
    return product;
  },

  // Create product
  createProduct: async (product: CreateProductDto): Promise<Product> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Product/Add`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(product),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Failed to create product");
    }
    return response.json();
  },

  // Update product
  updateProduct: async (
    id: number,
    product: UpdateProductDto
  ): Promise<void> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Product/Update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, ...product }),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Failed to update product");
    }
  },

  // Update status
  updateStatus: async (
    id: number,
    statusDto: UpdateStatusDto
  ): Promise<void> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Product/UpdateStatus`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, ...statusDto }),
    });
    if (!response.ok) throw new Error("Failed to update status");
  },

  // Update quantity
  updateQuantity: async (
    id: number,
    quantityDto: UpdateQuantityDto
  ): Promise<void> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Product/UpdateQuantity`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, ...quantityDto }),
    });
    if (!response.ok) throw new Error("Failed to update quantity");
  },

  // Toggle availability
  toggleAvailability: async (
    id: number,
    updatedBy: string
  ): Promise<{ availability: boolean }> => {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/Product/UpdateAvailability`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, updatedBy })
      }
    );
    if (!response.ok) throw new Error("Failed to toggle availability");
    return response.json();
  },

  // Delete product
  deleteProduct: async (id: number, deletedBy: string): Promise<boolean> => {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/Product/Delete`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, deletedBy })
      }
    );
    if (!response.ok) throw new Error("Failed to delete product");
    const result = await response.json();
    if (result !== true) throw new Error("Delete was not confirmed by server");
    return result;
  },

  // Get audit logs for a product
  getAuditLogs: async (productId: number): Promise<ProductAuditLog[]> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/products/${productId}/audit`);
    if (!response.ok) throw new Error("Failed to fetch audit logs");
    return response.json();
  },
};

// Ingredients API Service
export const ingredientsApi = {
  getIngredients: async (): Promise<Ingredient[]> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Ingredient/GetMyIngredients`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) throw new Error("Failed to fetch ingredients");
    return response.json();
  },

  createIngredient: async (ingredient: {
    code: string;
    name: string;
  }): Promise<Ingredient> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Ingredient/Add`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(ingredient),
    });
    if (!response.ok) throw new Error("Failed to create ingredient");
    return response.json();
  },

  updateIngredient: async (
    id: number,
    ingredient: Ingredient
  ): Promise<void> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Ingredient/Update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, ...ingredient }),
    });
    if (!response.ok) throw new Error("Failed to update ingredient");
  },

  deleteIngredient: async (id: number): Promise<boolean> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Ingredient/Delete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });
    if (!response.ok) throw new Error("Failed to delete ingredient");
    const result = await response.json();
    if (result !== true) throw new Error("Delete was not confirmed by server");
    return result;
  },
};

// Orders API Service
// Admin API is used for form schemas only
const ADMIN_API_URL = runtimeConfig.adminApiUrl;

export const ordersApi = {
  // Fetch orders from Supplier's OWN database (duplicated from Admin on creation)
  getOrders: async (): Promise<Order[]> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Order/GetBySupplier`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) throw new Error("Failed to fetch orders");
    return response.json();
  },

  getOrder: async (id: number): Promise<Order> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Order/GetById`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(id),
    });
    if (!response.ok) throw new Error("Failed to fetch order");
    return response.json();
  },

  getOrderById: async (id: number): Promise<Order> => {
    return ordersApi.getOrder(id);
  },

  getFormSchema: async (type: string): Promise<any> => {
    const response = await fetchWithAuth(`${ADMIN_API_URL}/api/formschemas/${type}`);
    if (!response.ok) throw new Error("Failed to fetch form schema");
    return response.json();
  },

  updateOrderStatus: async (
    id: number,
    status: string,
    changedBy: string = "Supplier",
    notes?: string
  ): Promise<void> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Order/UpdateStatus`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id, status }),
    });
    if (!response.ok) throw new Error("Failed to update order status");
  },

  getClientOrderLogs: async (id: number): Promise<any[]> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Order/GetOrderLogs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id }),
    });
    if (!response.ok) throw new Error("Failed to fetch order logs");
    return response.json();
  },

  updateOrder: async (order: {
    Id: number;
    dateRequired: string;
    total: number;
    formData: string;
  }): Promise<boolean> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Order/Update`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(order),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: "Failed to update order" }));
      throw new Error(error.message || "Failed to update order");
    }
    return response.json();
  },

  getEditHistory: async (orderId: number): Promise<OrderEditHistory[]> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Order/GetEditHistory`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: orderId }),
    });
    if (!response.ok) throw new Error("Failed to fetch edit history");
    return response.json();
  },

  getEditHistoryById: async (id: number): Promise<OrderEditHistory> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/Order/GetEditHistoryById`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(id),
    });
    if (!response.ok) throw new Error("Failed to fetch edit history record");
    return response.json();
  },

  // Legacy method - kept for backwards compatibility with NewOrder.tsx
  // TODO: Remove when supplier order creation is fully removed
  createOrder: async (order: CreateOrderDto): Promise<Order> => {
    const response = await fetchWithAuth(`${API_BASE_URL}/orders`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(order),
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || "Failed to create order");
    }
    return response.json();
  },
};

export default productsApi;
